import axios from "axios"
import { apiConfig } from "@/lib/config"

export class StatusAPI {
  static async get() {
    return (await axios.get("/", apiConfig)).data
  }
}

export class UsersAPI {
  static async get(id) {
    return (await axios.get(`/users/${id}`, apiConfig)).data
  }
  static async updateAvatar(userId, file) {
    const formData = new FormData()
    formData.append("avatar", file, file.name)

    return (await axios.put(`/users/${userId}`, formData, apiConfig)).data
  }

  static async assignCourses(userId, courseIds) {
    return (await axios.post(
      "/users/update_courses",
      {
        user_id: userId,
        course_ids: courseIds
      },
      apiConfig
    )).data
  }

  static async assignDocuments(userId, documentIds) {
    return (await axios.post(
      "/users/update_documents",
      {
        user_id: userId,
        document_ids: documentIds
      },
      apiConfig
    )).data
  }

  static async assignFamilies(userId, familyIds) {
    return (await axios.post(
      "/users/update_families",
      {
        user_id: userId,
        family_ids: familyIds
      },
      apiConfig
    )).data
  }

  // Returns the entire response, in order to get at the JWT token in the header
  static async login(email, password) {
    return await axios.post(
      "/users/sign_in",
      { user: { email, password } },
      {
        baseURL: apiConfig.baseURL
      }
    )
  }

  static async logout() {
    return (await axios.delete("/users/sign_out", apiConfig)).data
  }

  static async sendInvite(userId) {
    return await axios.post(
      `/users/send_invite`,
      { user_id: userId },
      apiConfig
    )
  }

  static async requestPasswordReset(email) {
    let data = await axios.post("/users/reset", { email: email }, apiConfig)

    console.log(data)
    return data
  }

  static async setPassword(token, password, password2) {
    return await axios.post(
      `/users/setup`,
      { token: token, password: password, password2: password2 },
      { baseURL: apiConfig.baseURL }
    )
  }

  static async checkValidPasswordResetToken(token) {
    let data = (await axios.get(
      "/users/token_validate",
      Object.assign({ params: { token: token } }, apiConfig)
    )).data

    return data
  }

  static async create(firstName, lastName, email, userType, phoneNumber) {
    return (await axios.post(
      `/users`,
      {
        first_name: firstName,
        last_name: lastName,
        email: email,
        phone_number: phoneNumber,
        user_type: userType
      },
      apiConfig
    )).data
  }

  static async update(params) {
    return (await axios.put(`/users/${params.id}`, params, apiConfig)).data
  }

  static async list(queryParams) {
    let config = apiConfig
    if (
      !(
        Object.entries(queryParams).length === 0 &&
        queryParams.constructor === Object
      )
    ) {
      config = Object.assign({ params: queryParams }, apiConfig)
    }

    let data = (await axios.get("/users", config)).data

    return data
  }

  static async disable(id) {
    return (await axios.delete(`/users/${id}`, apiConfig)).data
  }

  static async addNote(userId, note) {
    return (await axios.post(
      `/user_notes`,
      {
        user_id: userId,
        body: note
      },
      apiConfig
    )).data
  }

  static async proDFetch(userId) {
    return await axios.post(
      "/users/fetch_pro_d",
      { user_id: userId },
      apiConfig
    )
  }

  static async parseEnneagram(userId) {
    return await axios.post(
      "/users/parse_enneagram",
      { user_id: userId },
      apiConfig
    )
  }
}

export class CoursesAPI {
  static async list({ showTemplates, includeHidden, userId }) {
    let data = (await axios.get(
      "/courses",
      Object.assign(
        {
          params: {
            templates: showTemplates,
            include_hidden: includeHidden,
            user_id: userId
          }
        },
        apiConfig
      )
    )).data

    data.forEach(el => {
      el.learning_items.forEach(learning_item => {
        learning_item.responses.forEach(response => {
          if (response.options) {
            response.value = JSON.parse(response.value)
            response.options = JSON.parse(response.options)
          } else {
            response.options = { type: "", choices: [] }
          }
        })
      })
    })
    return data
  }
  static async get(id, { includeHidden }) {
    let data = (await axios.get(
      `/courses/${id}`,
      Object.assign({ params: { include_hidden: includeHidden } }, apiConfig)
    )).data

    data.learning_items.forEach(learning_item => {
      learning_item.responses.forEach(response => {
        if (response.options) {
          response.value = JSON.parse(response.value)
          response.options = JSON.parse(response.options)
        } else {
          response.options = { type: "", choices: [] }
        }
      })
    })
    return data
  }
  static async create({ badgeId, name, description, template, touched }) {
    return (await axios.post(
      `/courses`,
      { course: { badge_id: badgeId, name, description, template, touched } },
      apiConfig
    )).data
  }
  static async update(id, { name, description, badgeId, touched, folder }) {
    return (await axios.put(
      `/courses/${id}`,
      { name, description, badge_id: badgeId, touched, folder },
      apiConfig
    )).data
  }

  static async delete(id) {
    return (await axios.delete(`/courses/${id}`, apiConfig)).data
  }
  static async addItem(courseId, itemId) {
    return (await axios.post(
      `/courses/add_item`,
      {
        course_id: courseId,
        learning_item_id: itemId
      },
      apiConfig
    )).data
  }

  static async assignLearningItems(courseId, itemIds, newAsCopy) {
    return (await axios.post(
      `/courses/update_items`,
      {
        course_id: courseId,
        learning_item_ids: itemIds,
        new_as_copy: newAsCopy
      },
      apiConfig
    )).data
  }
}

export class LearningItemsAPI {
  static async list({ templates, userId, includeHidden }) {
    let data = (await axios.get(
      `/learning_items`,
      Object.assign(
        {
          params: {
            templates,
            user_id: userId,
            include_hidden: includeHidden
          }
        },
        apiConfig
      )
    )).data

    data.forEach(el => {
      el.responses.forEach(response => {
        response.value = JSON.parse(response.value)
        response.options = response.options ? JSON.parse(response.options) : ""
      })
    })
    return data
  }

  static async get(id) {
    return (await axios.get(`/learning_items/${id}`, apiConfig)).data
  }

  static async delete(id) {
    return (await axios.delete(`/learning_items/${id}`, apiConfig)).data
  }

  static async create({ name, description, body, badgeId }) {
    return (await axios.post(
      `/learning_items`,
      { name, description, body, template: true, badge_id: badgeId },
      apiConfig
    )).data
  }

  static async update(
    id,
    { name, description, body, hidden, status, badgeId }
  ) {
    return (await axios.put(
      `/learning_items/${id}`,
      {
        learning_item: {
          name,
          description,
          body,
          hidden,
          status,
          badge_id: badgeId
        }
      },
      apiConfig
    )).data
  }
}

export class LearningItemsResponsesAPI {
  static async create(learningItemId, options, key) {
    return (await axios.post(
      `/learning_item_responses`,
      {
        learning_item_response: {
          learning_item_id: learningItemId,
          options: JSON.stringify(options),
          key: key
        }
      },
      apiConfig
    )).data
  }

  static async update(id, { learningItemId, key, value, type, options }) {
    return (await axios.put(
      `/learning_item_responses/${id}`,
      {
        learning_item_response: {
          learning_item_id: learningItemId,
          key,
          value: JSON.stringify(value),
          type,
          options: JSON.stringify(options)
        }
      },
      apiConfig
    )).data
  }

  static async delete(id) {
    return (await axios.delete(`/learning_item_responses/${id}`, apiConfig))
      .data
  }
}

export class FamiliesAPI {
  static async list() {
    return (await axios.get("/families", apiConfig)).data
  }

  static async get(id) {
    return (await axios.get(`/families/${id}`, apiConfig)).data
  }

  static async create({ name }) {
    return (await axios.post(`/families`, { name }, apiConfig)).data
  }
}

export class ReflectionsAPI {
  static async list({ userId }) {
    return (await axios.get(
      "/reflections",
      Object.assign({ params: { user_id: userId } }, apiConfig)
    )).data
  }

  static async get(id) {
    return (await axios.get(`/reflections/${id}`, apiConfig)).data
  }

  static async create({ userId, text, title }) {
    return (await axios.post(
      "/reflections",
      {
        reflection: { user_id: userId, text, title }
      },
      apiConfig
    )).data
  }

  static async update(id, { title, text }) {
    return (await axios.put(`/reflections/${id}`, { title, text }, apiConfig))
      .data
  }
  static async delete(id) {
    return (await axios.delete(`/reflections/${id}`, apiConfig)).data
  }
}

export class DocumentsAPI {
  static async list() {
    return (await axios.get("/documents", apiConfig)).data
  }
  static async get(id) {
    return (await axios.get(`/documents/${id}`, apiConfig)).data
  }
  static async create(file) {
    const formData = new FormData()
    formData.append("source", file, file.name)
    formData.append("name", file.name)
    formData.append("learning_item_id", file.learning_item_id)
    return (await axios.post("/documents", formData, apiConfig)).data
  }
  static async delete(id) {
    return (await axios.delete(`/documents/${id}`, apiConfig)).data
  }

  static async getDocumentsByUserId(userId) {
    return (await axios.get(`/documents?user_id=${userId}`, apiConfig)).data
  }

  static async update({ id, name, folder }) {
    return (await axios.put(`/documents/${id}`, { name, folder }, apiConfig))
      .data
  }
}

export class BadgesAPI {
  static async list() {
    return (await axios.get("/badges", apiConfig)).data
  }

  static async create(file) {
    const formData = new FormData()
    formData.append("source", file, file.name)
    formData.append("name", file.name)
    return (await axios.post("/badges", formData, apiConfig)).data
  }

  static async delete(id) {
    return (await axios.delete(`/badges/${id}`, apiConfig)).data
  }
}

export class BudgetsAPI {
  static async list(userId) {
    return (await axios.get(
      "/budgets",
      Object.assign(userId ? { params: { user_id: userId } } : {}, apiConfig)
    )).data
  }

  static async get(id) {
    return (await axios.get(`/budgets/${id}`, apiConfig)).data
  }

  static async create(body) {
    return (await axios.post("/budgets", body, apiConfig)).data
  }

  static async update(id, body) {
    return (await axios.put(`/budgets/${id}`, body, apiConfig)).data
  }

  static async delete(id) {
    return (await axios.delete(`/budgets/${id}`, apiConfig)).data
  }
}

export class AssessmentsAPI {
  static async list() {
    return (await axios.get("/assessments", apiConfig)).data
  }
  static async get(id) {
    return (await axios.get(`/assessments/${id}`, apiConfig)).data
  }

  static async addEnneagram(
    userId,
    primaryType,
    secondaryType,
    tertiaryType,
    selectedType,
    visible
  ) {
    console.log("visible: " + visible)
    return (await axios.post(
      `/enneagram_results`,
      {
        enneagram_result: {
          user_id: userId,
          primary_type: primaryType,
          secondary_type: secondaryType,
          tertiary_type: tertiaryType,
          selected_type: selectedType,
          visible: visible
        }
      },
      apiConfig
    )).data
  }

  static async uploadEnneagramDoc(userId, file) {
    console.log("uploading in api...")
    const formData = new FormData()
    formData.append("document", file, file.name)
    formData.append("user_id", userId)
    return (await axios.post("/enneagram_results/upload", formData, apiConfig))
      .data
  }

  static async addProD(
    userId,
    name,
    mission,
    competency,
    style,
    rank,
    visible
  ) {
    return (await axios.post(
      `/pro_d_results`,
      {
        pro_d_result: {
          user_id: userId,
          name: name,
          mission: mission,
          competency: competency,
          style: style,
          rank: rank,
          visible: visible
        }
      },
      apiConfig
    )).data
  }

  static async updateProDVisibility(userId, visible) {
    return (await axios.put(
      `/pro_d_results/visibility`,
      { user_id: userId, visible: visible },
      apiConfig
    )).data
  }

  static async updateViaVisibility(userId, visible) {
    return (await axios.put(
      `/via_results/visibility`,
      { user_id: userId, visible: visible },
      apiConfig
    )).data
  }

  static async add4Mat(userId, q1, q2, q3, q4, quadrant, visible) {
    return (await axios.post(
      `/four_mat_results`,
      {
        four_mat_result: {
          user_id: userId,
          q1: q1,
          q2: q2,
          q3: q3,
          q4: q4,
          quadrant: quadrant,
          visible: visible
        }
      },
      apiConfig
    )).data
  }

  static async addVia(userId, name, value, subOrder, visible) {
    console.log("visible2: " + visible)
    console.log("value2: " + value)
    return (await axios.post(
      `/via_results`,
      {
        via_result: {
          user_id: userId,
          name: name,
          value: value,
          sub_order: subOrder,
          visible: visible
        }
      },
      apiConfig
    )).data
  }
}

export class MessagesAPI {
  static async list({ userId }) {
    return (await axios.get(
      "/messages",
      Object.assign(
        {
          params: { user_id: userId }
        },
        apiConfig
      )
    )).data.messages
  }
  static async provision({ userId }) {
    return (await axios.post(
      `/messages/provision`,
      {
        user_id: userId
      },
      apiConfig
    )).data
  }
  static async send({ userId, body }) {
    return (await axios.post(
      `/messages`,
      {
        user_id: userId,
        message_body: body
      },
      apiConfig
    )).data
  }
}

export class NotificationsAPI {
  static async list() {
    return (await axios.get("/notifications", apiConfig)).data
  }

  static async markAsRead(id) {
    return (await axios.put(
      "/notifications/mark_as_read",
      { id: id },
      apiConfig
    )).data
  }
}

export class SystemMessagesAPI {
  static async list(userId) {
    return (await axios.get(
      "/system_messages",
      Object.assign(
        {
          params: {
            user_id: userId
          }
        },
        apiConfig
      )
    )).data
  }
  static async add(toUserId, body) {
    return (await axios.post(
      `/system_messages`,
      { user_id: toUserId, body },
      apiConfig
    )).data
  }
  static async update(id, body) {
    return (await axios.put(`/system_messages/${id}`, { body }, apiConfig)).data
  }

  static async delete(id) {
    return (await axios.delete(`/system_messages/${id}`, apiConfig)).data
  }
}

export class NudgesAPI {
  static async list(menteeId) {
    return (await axios.get(
      "/nudges",
      Object.assign(
        {
          params: {
            target_id: menteeId
          }
        },
        apiConfig
      )
    )).data
  }

  static async nudge(nudgeableType, nudgeableId, menteeId, mentorId, text) {
    const payload = {
      nudgeable_id: nudgeableId,
      nudgeable_type: nudgeableType,
      target_id: menteeId,
      sendor_id: mentorId,
      text: text
    }

    return (await axios.post("/nudges", payload, apiConfig)).data
  }
}

export class ToolsAPI {
  static async list(userId) {
    let params = userId
      ? Object.assign(
          {
            params: {
              user_id: userId
            }
          },
          apiConfig
        )
      : apiConfig
    return (await axios.get("/tools", params)).data
  }

  static async listAssignments(userId) {
    let params = userId
      ? Object.assign(
          {
            params: {
              user_id: userId
            }
          },
          apiConfig
        )
      : apiConfig
    return (await axios.get("/tool_assignments", params)).data
  }

  static async assign(tool, userId) {
    return (await axios.post(
      "/tool_assignments",
      {
        tool: tool,
        user_id: userId
      },
      apiConfig
    )).data
  }

  static async unassign(toolId) {
    return (await axios.delete(`/tool_assignments/${toolId}`, apiConfig)).data
  }
}
